import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, UserMinus, Search, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader, EmptyState } from "@/components/common";
import { PersonCard } from "@/pages/People";
import { useApi, errMsg } from "@/lib/api";

export default function Network() {
  const api = useApi();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => api("get", "/connections").then(setData), [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (method, url, msg) => {
    if (msg === "Connection removed" && !window.confirm("Remove this connection?")) return;
    try { await api(method, url); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <Loader />;

  const grid = (items, right, empty) => items.length === 0 ? empty : (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((it, i) => <PersonCard key={it.user.user_id} p={it.user} index={i} right={right(it.user)} />)}
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Your network</p><h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Network</h1></div>
        <Button asChild variant="outline" data-testid="network-find-people"><Link to="/people"><Search size={16} />Find people</Link></Button>
      </div>
      <Tabs defaultValue={data.received.length ? "received" : "connections"} className="mt-8">
        <TabsList>
          <TabsTrigger value="connections" data-testid="network-tab-connections">Connections ({data.connections.length})</TabsTrigger>
          <TabsTrigger value="received" data-testid="network-tab-received">Invitations ({data.received.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="network-tab-sent">Sent ({data.sent.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="connections" className="mt-6">
          {grid(data.connections, (u) => (
            <div className="flex flex-col gap-2">
              <Button size="icon" onClick={() => nav(`/messages/${u.user_id}`)} data-testid={`connection-message-${u.user_id}`} aria-label="Message"><MessageSquare size={16} /></Button>
              <Button size="icon" variant="outline" onClick={() => act("delete", `/connections/${u.user_id}`, "Connection removed")} data-testid={`connection-remove-${u.user_id}`} aria-label="Remove"><UserMinus size={16} /></Button>
            </div>
          ), <EmptyState testId="connections-empty" title="No connections yet" text="Invite technicians and companies to build your network." action={<Button asChild data-testid="connections-empty-find"><Link to="/people">Browse people</Link></Button>} />)}
        </TabsContent>
        <TabsContent value="received" className="mt-6">
          {grid(data.received, (u) => (
            <div className="flex flex-col gap-2">
              <Button size="icon" onClick={() => act("post", `/connections/${u.user_id}/accept`, "Invitation accepted")} data-testid={`invite-accept-${u.user_id}`} aria-label="Accept"><Check size={16} /></Button>
              <Button size="icon" variant="outline" onClick={() => act("delete", `/connections/${u.user_id}`, "Invitation declined")} data-testid={`invite-decline-${u.user_id}`} aria-label="Decline"><X size={16} /></Button>
            </div>
          ), <EmptyState testId="received-empty" title="No pending invitations" />)}
        </TabsContent>
        <TabsContent value="sent" className="mt-6">
          {grid(data.sent, (u) => (
            <Button size="sm" variant="outline" onClick={() => act("delete", `/connections/${u.user_id}`, "Invitation withdrawn")} data-testid={`invite-cancel-${u.user_id}`}>Withdraw</Button>
          ), <EmptyState testId="sent-empty" title="No sent invitations" />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
